/**
 * Password complexity policy, shared between the Gateway (server-side
 * validation, source of truth) and apps/web (client-side live checklist
 * as the user types). Defined here instead of duplicated in both places
 * so the rules can never drift out of sync.
 *
 * Each rule is a pure predicate over a candidate password string, plus a
 * short id (stable, safe to use as a React key) and a human-readable
 * label. The Gateway runs every rule and rejects on the first failure;
 * apps/web (Phase 12) runs every rule on each keystroke and renders one
 * checkmark/cross per rule.
 */
const MIN_LENGTH = 12;
const MAX_LENGTH = 128;
// Blocks the small set of characters that make sense to reject outright
// in a password field: nothing here is a serious injection defense (the
// password is always handled as an opaque hashed value, never
// interpolated into a query or shell command), it just refuses obvious
// script/markup/SQL-meta payloads so a password field can't be used as a
// convenient XSS/SQLi probe against admin tooling that might render
// audit logs or usernames unescaped somewhere down the line.
const SUSPICIOUS_CODE_PATTERN = /<script|<\/script|javascript:|on\w+\s*=|\$\{|`|--|;--|\/\*/i;
export const PASSWORD_POLICY_RULES = [
    {
        id: "min-length",
        label: `At least ${MIN_LENGTH} characters`,
        test: (password) => password.length >= MIN_LENGTH,
    },
    {
        id: "max-length",
        label: `At most ${MAX_LENGTH} characters`,
        test: (password) => password.length <= MAX_LENGTH,
    },
    {
        id: "lowercase",
        label: "At least one lowercase letter",
        test: (password) => /[a-z]/.test(password),
    },
    {
        id: "uppercase",
        label: "At least one uppercase letter",
        test: (password) => /[A-Z]/.test(password),
    },
    {
        id: "number",
        label: "At least one number",
        test: (password) => /\d/.test(password),
    },
    {
        id: "symbol",
        label: "At least one special character",
        test: (password) => /[^A-Za-z0-9]/.test(password),
    },
    {
        id: "no-suspicious-code",
        label: "No script-like or code-like content",
        test: (password) => !SUSPICIOUS_CODE_PATTERN.test(password),
    },
];
/**
 * Runs every rule against a candidate password. Used by apps/web (Phase
 * 12) to render a live per-rule checklist as the user types: does not
 * short-circuit on the first failure, unlike server-side validation,
 * because the UI needs the pass/fail state of every rule at once.
 */
export function evaluatePasswordPolicy(password) {
    return PASSWORD_POLICY_RULES.map((rule) => ({
        ruleId: rule.id,
        passed: rule.test(password),
    }));
}
/** True only if every rule passes. Used by the Gateway's Zod schema via `.refine()`. */
export function isPasswordPolicyCompliant(password) {
    return PASSWORD_POLICY_RULES.every((rule) => rule.test(password));
}
//# sourceMappingURL=passwordPolicy.js.map